import React, { useState } from "react";
import { Icon } from "@iconify/react";
import {
  ButtonSecondary,
  HStack,
  PrimaryCTAButton,
  VStack,
} from "../../../components/utils";
import { Input, Label } from "../../../components/shared/InputField";
import { MultiSelectV2 } from "../../../components/shared/MultiSelectV2";
import {
  HissaReportType,
  ScheduledReport,
  UpdateScheduleRequest,
} from "../../../types/common";
import {
  useGetScheduledReports,
  useUpdateScheduledReport,
  useDeactivateScheduledReport,
} from "../../../queries/scheduledReports";
import { useGetAssociatedUsers } from "../../../queries/usersRoles";
import { formatDisplayDate } from "../../../utils/date";
import { useFilteredAssociatedUsers } from "./ScheduleSection";

interface Props {
  reportType: HissaReportType;
  reportName: string;
  onClose: () => void;
}

function ScheduleRow({
  schedule,
  users,
}: {
  schedule: ScheduledReport;
  users: { id: string; name: string }[];
}) {
  const { mutate: update, isLoading: updating } = useUpdateScheduledReport();
  const { mutate: deactivate, isLoading: deactivating } =
    useDeactivateScheduledReport();
  const [editing, setEditing] = useState(false);
  const [intervalDays, setIntervalDays] = useState<string>(
    String(schedule.intervalDays)
  );
  const [selectedNames, setSelectedNames] = useState<string[]>(
    users.filter((u) => schedule.recipients.includes(u.id)).map((u) => u.name)
  );

  const userOptions = users.map((u) => ({ value: u.name }));

  const recipientNames =
    users
      .filter((u) => schedule.recipients.includes(u.id))
      .map((u) => u.name)
      .join(", ") || "—";

  const handleToggleActive = () => {
    if (schedule.isActive) {
      deactivate(schedule.id);
    } else {
      update({ id: schedule.id, payload: { isActive: true } });
    }
  };

  const handleSave = () => {
    const parsed = parseInt(intervalDays, 10);
    const days = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    const ids = users
      .filter((u) => selectedNames.includes(u.name) && u.id)
      .map((u) => u.id);
    const payload: UpdateScheduleRequest = {
      intervalDays: days,
      recipients: ids,
    };
    update(
      { id: schedule.id, payload },
      { onSuccess: () => setEditing(false) }
    );
  };

  const nextRun = schedule.nextRunAt
    ? formatDisplayDate(new Date(schedule.nextRunAt))
    : "—";
  const lastRun = schedule.lastRunAt
    ? formatDisplayDate(new Date(schedule.lastRunAt))
    : "Never";

  return (
    <div className="border border-gray-100 rounded-lg p-4 ">
      {/* Header row */}
      <HStack className="items-center justify-between mb-3">
        <HStack className="items-center gap-2">
          <button
            type="button"
            onClick={handleToggleActive}
            disabled={updating}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
              schedule.isActive ? "bg-orange-501" : "bg-gray-300"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                schedule.isActive ? "translate-x-4" : "translate-x-0"
              }`}
            />
          </button>
          <span className="text-xs font-medium text-gray-600">
            {schedule.isActive ? "Active" : "Paused"}
          </span>
        </HStack>

        <HStack className="items-center gap-2">
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs text-orange-501 hover:underline"
            >
              Edit
            </button>
          )}
        </HStack>
      </HStack>

      {/* Info / edit rows */}
      {editing ? (
        <VStack className="gap-3">
          <div>
            <Label className="text-xs font-normal">Recipients</Label>
            <MultiSelectV2
              _className="w-full"
              multiple
              placeholder="-- Select users --"
              options={userOptions}
              optionValues={selectedNames}
              handleSelection={setSelectedNames}
            />
          </div>
          <div className="w-36">
            <Label className="text-xs font-normal">Repeat every (days)</Label>
            <Input
              type="text"
              inputMode="numeric"
              value={intervalDays}
              onChange={(e) =>
                setIntervalDays(e.target.value.replace(/[^0-9]/g, ""))
              }
              onBlur={() => {
                const parsed = parseInt(intervalDays, 10);
                setIntervalDays(
                  String(Number.isNaN(parsed) || parsed < 1 ? 1 : parsed)
                );
              }}
            />
          </div>
          <HStack className="gap-4 justify-end">
            <ButtonSecondary
              type="button"
              onClick={() => setEditing(false)}
              className="text-xs text-gray-500 hover:underline"
            >
              Cancel
            </ButtonSecondary>
            <PrimaryCTAButton
              type="button"
              onClick={handleSave}
              disabled={updating || selectedNames.length === 0}
              className="text-xs font-medium text-white bg-orange-501 px-3 py-1 rounded hover:bg-orange-600 disabled:opacity-50"
            >
              {updating ? "Saving…" : "Save"}
            </PrimaryCTAButton>
          </HStack>
        </VStack>
      ) : (
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs text-gray-600">
          <div>
            <span className="text-gray-400">Recipients: </span>
            {recipientNames}
          </div>
          <div>
            <span className="text-gray-400">Every: </span>
            {schedule.intervalDays} day{schedule.intervalDays !== 1 ? "s" : ""}
          </div>
          <div>
            <span className="text-gray-400">Last run: </span>
            {lastRun}
          </div>
          <div>
            <span className="text-gray-400">Next run: </span>
            {nextRun}
          </div>
        </div>
      )}
    </div>
  );
}

const ScheduleManagementDialog: React.FC<Props> = ({
  reportType,
  reportName,
  onClose,
}) => {
  const { data: allSchedules = [], isLoading } = useGetScheduledReports();
  const { data: associatedUsers = [] } = useGetAssociatedUsers();

  const { data: _associatedUsers = [] } =
    useFilteredAssociatedUsers(associatedUsers);

  const users = _associatedUsers
    .filter((u) => u.userId)
    .map((u) => ({ id: String(u.userId), name: u.name }));
  const schedules = allSchedules.filter((s) => s.reportType === reportType);

  return (
    <div className="w-[600px] mx-auto bg-white rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-6 text-lg font-medium border-b py-5">
        <div>
          <div>Scheduled Runs</div>
          <div className="text-sm font-normal text-gray-500 mt-0.5">
            {reportName}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer text-gray-500 hover:text-gray-800"
        >
          <Icon icon="mdi:close" width={20} />
        </button>
      </div>

      {/* Body */}
      <VStack className="p-6 gap-3 overflow-y-auto max-h-[480px]">
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading schedules…</div>
        ) : schedules.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-gray-400">
            <Icon icon="mdi:calendar-clock-outline" width={36} />
            <div className="text-sm">No schedules for this report.</div>
            <div className="text-xs text-gray-400">
              Click `Generate Report` and enable scheduling to add one.
            </div>
          </div>
        ) : (
          schedules.map((s) => (
            <ScheduleRow key={s.id} schedule={s} users={users} />
          ))
        )}
      </VStack>
    </div>
  );
};

export default ScheduleManagementDialog;
